import { createContext } from 'react';
import {
	ModelApisForm,
	ApisFormContextType,
	ItemList,
	SelectOptions,
	DocumentObject,
	ModelApisInfant,
	FormDataApisInfant,
	ApisErrorType,
	FormDataApis,
	ErrorApisObject,
	FullName
} from './ApisForm.model';
import {
	DocumentForm,
	FormDocumentationRequired,
	ModelDocumentationRequired,
	REQUIRED_FIELD_TRANSFORMER,
	RequiredField,
	RequiredFieldOptions,
	RequiredFieldTypes,
	RequiredFieldTypesOrigin,
	TYPE_CODE_DOCUMENT
} from './components/DocumentationRequired/DocumentationRequired.model';
import { FormPassengerAddress, ModelPassengerAddress } from './components/PassengerAddress/PassengerAddress.model';
import { ModelPersonalData, FormPersonalData } from './components/PersonalData/PersonalData.model';
import { FormContactData, ModelContactData } from './components/ContactData';
import {
	AdditionalInfoFields,
	ApisConstants,
	DocMandatoryFields,
	EmergencyInfoFields,
	InfantFields,
	PassengerAddressFields,
	PersonalDataFields,
	RequiredFieldsType,
	CheckboxInfoFieldsRequired,
	DocVisaFields,
	ApisError,
	DocVisaFieldsInfant,
	errorApisTypes
} from './ApisForm.constants';
import { UseFormSetError } from 'react-hook-form';
import { TFunction } from 'i18next';
export const getRequiredFields = ({
	passengerAddress,
	contactData,
	infant,
	isPrivacyStatement
}: ModelApisForm): RequiredFieldsType => {
	let requiredFields: RequiredFieldsType = [
		...Object.values(PersonalDataFields),
		...Object.values(DocMandatoryFields),
		...[
			AdditionalInfoFields.EMAIL,
			AdditionalInfoFields.NUMBER,
			AdditionalInfoFields.PHONE,
			CheckboxInfoFieldsRequired.CONFIRM_EMERGENCY_DATA
		]
	];
	const fieldsPassengerAddress = [
		PassengerAddressFields.ADDRESS,
		PassengerAddressFields.CITY,
		PassengerAddressFields.STATE,
		PassengerAddressFields.ZIP_CODE
	];
	const fieldEmergencyInfo = [EmergencyInfoFields.NAME, EmergencyInfoFields.NUMBER, EmergencyInfoFields.PHONE];
	const fieldsInfant = Object.values(InfantFields);
	if (passengerAddress) requiredFields = [...requiredFields, ...fieldsPassengerAddress];
	if (contactData.emergencyInfo.name.required) requiredFields = [...requiredFields, ...fieldEmergencyInfo];
	if (infant) requiredFields = [...requiredFields, ...fieldsInfant];
	if (isPrivacyStatement)
		requiredFields = [...requiredFields, ...[CheckboxInfoFieldsRequired.US_PRIVACY_EMERGENCY_DATA]];
	return requiredFields;
};

export const modelToDataFormPassengerAddress = (
	passengerAddress: ModelPassengerAddress,
	listStates: ItemList[]
): FormPassengerAddress => {
	const { address, zipCode, city, state, checkAddressForAll = false } = passengerAddress;
	return {
		address: address.value,
		zipCode: zipCode.value,
		city: city.value,
		state: state ? (searchOption(getOptions(listStates), state.value) as SelectOptions) : undefined,
		checkAddressForAll: checkAddressForAll
	};
};

export const modelToDataFormPersonalData = (
	personalData: ModelPersonalData,
	listCountries: ItemList[]
): FormPersonalData => {
	const { name, surname, birthDate, gender, nationality, country } = personalData;
	return {
		name: name.value,
		surname: surname.value,
		birthDate: birthDate.value,
		gender: gender.value,
		nationality: searchOption(getOptions(listCountries), nationality.value) as SelectOptions,
		country: country ? (searchOption(getOptions(listCountries), country.value) as SelectOptions) : undefined
	};
};

export const findDocumentObject = (documents: DocumentObject[], code: string) => {
	return documents.find((document) => (document.type as ItemList).code === code);
};

const createTypeObject = (type: ItemList | undefined) =>
	type?.code ? { value: type.code, label: type.description } : undefined;
const createMandatoryDocumentObject = (document: DocumentObject) => ({
	type: createTypeObject(document.type as ItemList),
	number: document.number,
	expiryDate: document.expiryDate,
	issuerCountry: document.country?.value ? document.country : undefined
});
const createVisaDocumentObject = (document: DocumentObject) => ({
	type: document.type as SelectOptions,
	number: document.number,
	expiryDate: document.expiryDate,
	issuerCountry: document.country?.value ? document.country : undefined
});
export const modelToDataFormDocumentationRequired = ({
	mandatory,
	other
}: ModelDocumentationRequired): FormDocumentationRequired => {
	const mandatoryDocument: DocumentObject = findDocumentObject(mandatory, ApisConstants.PASSPORT) || mandatory[0];
	const visaDocument: DocumentObject | undefined = findDocumentObject(other ?? [], ApisConstants.VISA);

	return {
		mandatory: createMandatoryDocumentObject(mandatoryDocument),
		visa: visaDocument ? createVisaDocumentObject(visaDocument) : undefined,
		other: other ? { type: '', number: '', expiryDate: '' } : undefined
	};
};

export const getPhoneLabel = (phoneCode: string, countryCodes: ItemList[]): string => {
	return countryCodes.find((countryCode) => countryCode.code === phoneCode)?.description || '';
};

export const modelToDataFormContactData = (
	{ additionalInfo, emergencyInfo }: ModelContactData,
	countryCodes: ItemList[]
): FormContactData => {
	const mapPhone = (phone: { value: string }) =>
		phone.value ? { value: phone.value, label: getPhoneLabel(phone.value, countryCodes) } : undefined;
	const isCheck = (check: boolean) => (check ? check : false);
	return {
		additionalInfo: {
			email: additionalInfo.email.value,
			phone: mapPhone(additionalInfo.phone),
			number: additionalInfo.number.value,
			checkInfoAll: isCheck(additionalInfo.checkInfoAll)
		},
		emergencyInfo: {
			phone: mapPhone(emergencyInfo.phone),
			number: emergencyInfo.number.value,
			name: emergencyInfo.name.value,
			checkEmergencyAll: isCheck(emergencyInfo.checkEmergencyAll)
		}
	};
};

export const optionsUseForm = (
	{ personalData, passengerAddress, documentationRequired, contactData, infant }: ModelApisForm,
	countryCodes: ItemList[],
	listStates: ItemList[],
	listCountries: ItemList[]
) => {
	return {
		defaultValues: {
			personalData: modelToDataFormPersonalData(personalData, listCountries),
			passengerAddress: passengerAddress ? modelToDataFormPassengerAddress(passengerAddress, listStates) : undefined,
			contactData: modelToDataFormContactData(contactData, countryCodes),
			documentationRequired: modelToDataFormDocumentationRequired(documentationRequired),
			infant: infant ? modelToDataFormPassengerInfant(infant, listCountries) : undefined
		}
	};
};

export const ApisFormContext = createContext<ApisFormContextType>({
	listOptions: {
		nationalities: undefined,
		countries: undefined,
		states: undefined,
		countryCode: undefined,
		mandatoryDocsTypes: undefined,
		visaDocsTypes: undefined,
		otherDocsTypes: undefined
	},
	documentationRequired: {
		mandatory: []
	},
	isFormDisabled: false,
	isPrivacyStatement: false,
	apisErrors: [],
	id: 0,
	bookingInfoPassenger: {
		name: '',
		surname: '',
		infant: {
			name: '',
			surname: ''
		}
	}
});

export const getOptions = (items: ItemList[]): SelectOptions[] => {
	return items.map((item) => ({
		value: item.code,
		label: item.description
	}));
};

export const searchOption = (options: SelectOptions[], value: string): SelectOptions | undefined => {
	const search = (op: SelectOptions) => {
		const upperCase = (str: string) => str.toUpperCase();
		const v = upperCase(value);
		return upperCase(op.value) === v || upperCase(op.label) === v ? op : undefined;
	};
	const option = options.find(search);
	return option ? option : undefined;
};
export const transformRequiredFieldsType = (type: RequiredFieldTypesOrigin): RequiredFieldTypes | undefined =>
	REQUIRED_FIELD_TRANSFORMER[type];

export const transformRequiredFields = (requiredFieldsOrigin: RequiredFieldTypesOrigin[] = []) => {
	const requiredFields: RequiredField = {
		number: false,
		expiryDate: false,
		issuerCountry: false
	};
	requiredFieldsOrigin.forEach((type) => {
		const newType = transformRequiredFieldsType(type);
		if (newType) requiredFields[newType] = true;
	});

	return requiredFields;
};

export const transformRequiredFieldsOptions = (
	requiredFieldsOriginOptions: RequiredFieldOptions[] | undefined,
	countries: SelectOptions[]
) => {
	requiredFieldsOriginOptions?.forEach((element) => {
		const newType = transformRequiredFieldsType(element.field);
		if (newType) {
			element.type = newType;
			if (element.values.length === 1) {
				element.defaultValues = searchOption(countries, element.values[0]);
			}
		}
	});
	return requiredFieldsOriginOptions;
};

export const minMaxDateExpiryDate = (numMaxDay: number = 10) => {
	const minDate = new Date();
	const pastDate = new Date();
	const maxDate = new Date(pastDate.setFullYear(pastDate.getFullYear() + numMaxDay));
	return { minDate, maxDate };
};

export const modelToDataFormPassengerInfant = (
	infant: ModelApisInfant,
	listCountries: ItemList[]
): FormDataApisInfant => {
	return {
		personalData: modelToDataFormPersonalData(infant.personalData, listCountries),
		documentationRequired: modelToDataFormDocumentationRequired(infant.documentationRequired)
	};
};

export const getModelInfant = (isInfant: boolean) => (isInfant ? 'infant.' : '');

const errorHandlers: Partial<
	Record<
		ApisErrorType,
		(setError: UseFormSetError<FormDataApis>, t: TFunction<['common', 'apis-error']>, infant?: boolean) => void
	>
> = {
	[ApisError.TM_VISA_REQUIRED]: (setError, t, existInfant = false) => {
		if (existInfant) {
			setError(DocVisaFieldsInfant.ISSUER_COUNTRY, { message: t('apis-error:VISA_ISSUER_COUNTRY_VALID_DESTINATION') });
			setError(DocVisaFieldsInfant.NUMBER, { message: t('apis-error:REVIEW_THIS_FIELD') });
			setError(DocVisaFieldsInfant.EXPIRY_DATE, { message: t('apis-error:REVIEW_THIS_FIELD') });
		}
		setError(DocVisaFields.ISSUER_COUNTRY, { message: t('apis-error:VISA_ISSUER_COUNTRY_VALID_DESTINATION') });
		setError(DocVisaFields.NUMBER, { message: t('apis-error:REVIEW_THIS_FIELD') });
		setError(DocVisaFields.EXPIRY_DATE, { message: t('apis-error:REVIEW_THIS_FIELD') });
	},
	[ApisError.TM_PSPT_VALIDITY]: (setError, t, existInfant = false) => {
		if (existInfant) setError(InfantFields.EXPIRY_DATE, { message: t('apis-error:TM_PSPT_VALIDITY') });
		setError(DocMandatoryFields.EXPIRY_DATE, { message: t('apis-error:TM_PSPT_VALIDITY') });
	},
	[ApisError.API_DOCUMENT_EXPIRY_DATE]: (setError, t, existInfant = false) => {
		if (existInfant) setError(InfantFields.EXPIRY_DATE, { message: t('apis-error:TM_PSPT_VALIDITY') });
		setError(DocMandatoryFields.EXPIRY_DATE, { message: t('apis-error:TM_PSPT_VALIDITY') });
	},
	[ApisError.PSPT_VALIDITY_3_MONTHS]: (setError, t, existInfant = false) => {
		if (existInfant) setError(InfantFields.EXPIRY_DATE, { message: t('apis-error:PSPT_VALIDITY_3_MONTHS') });
		setError(DocMandatoryFields.EXPIRY_DATE, { message: t('apis-error:PSPT_VALIDITY_3_MONTHS') });
	},
	[ApisError.PSPT_VALIDITY_6_MONTHS]: (setError, t, existInfant = false) => {
		if (existInfant) setError(InfantFields.EXPIRY_DATE, { message: t('apis-error:PSPT_VALIDITY_6_MONTHS') });
		setError(DocMandatoryFields.EXPIRY_DATE, { message: t('apis-error:PSPT_VALIDITY_6_MONTHS') });
	}
};

export const controlApisErrors = (
	apisErrors: ErrorApisObject[],
	setError: UseFormSetError<FormDataApis>,
	t: TFunction<['common', 'apis-error']>,
	existInfant: boolean = false
) => {
	apisErrors.forEach((error) => {
		const handler = errorHandlers[error.code];
		if (handler) {
			handler(setError, t, existInfant);
		}
	});
};

export const containsApisError = (apisErrors: ErrorApisObject[] | undefined, error?: ApisErrorType): boolean => {
	if (error) return Array.isArray(apisErrors) && apisErrors.some((err) => err.code === error);
	else return Array.isArray(apisErrors) && apisErrors.some((err) => errorApisTypes.includes(err.code));
};

export const isValidDoc = (document: DocumentForm) => {
	const caseNotCheckExpiryDateField = [TYPE_CODE_DOCUMENT.REDRESSNUMBER, TYPE_CODE_DOCUMENT.KNOWNTRAVELERNUMBER];
	const issuerCountry =
		typeof document.issuerCountry === 'string' ? document.issuerCountry : document.issuerCountry?.value;
	const typeValue = typeof document.type === 'string' ? document.type : document.type?.value;

	if (typeValue && caseNotCheckExpiryDateField.includes(typeValue)) {
		return document.number && document.issuerCountry && issuerCountry;
	} else {
		return Object.values(document).every((valor) => valor && valor !== '');
	}
};

export const getFullName = ({ bookingInfoPassenger, existInfant = false }: FullName): string => {
	const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
	const formatName = ({ name, surname }: { name?: string; surname?: string }) =>
		[name, surname]
			.filter((part): part is string => Boolean(part))
			.map(capitalize)
			.join(' ');

	return existInfant && bookingInfoPassenger.infant
		? formatName(bookingInfoPassenger.infant)
		: formatName(bookingInfoPassenger);
};
